/**
 * SEOManager - Gestione dinamica meta tags e JSON-LD per GEO
 * Usa react-helmet-async per SSR-friendly (o client-side in SPA)
 */

import { Helmet, HelmetProvider } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  jsonLd?: Record<string, any>;
  ogType?: 'website' | 'article' | 'product';
  ogImage?: string;
  ogUrl?: string;
  children?: React.ReactNode;
}

export default function SEOManager({
  title,
  description,
  canonical,
  jsonLd,
  ogType = 'website',
  ogImage,
  ogUrl,
  children
}: SEOProps) {
  const siteUrl = 'https://sealevel.it';
  const fullCanonical = canonical ? `${siteUrl}${canonical}` : siteUrl;

  return (
    <HelmetProvider>
      <Helmet>
        {/* Basic SEO */}
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={fullCanonical} />

        {/* Open Graph */}
        <meta property="og:type" content={ogType} />
        <meta property="og:url" content={ogUrl || fullCanonical} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        <meta property="og:locale" content="it_IT" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        {ogImage && <meta name="twitter:image" content={ogImage} />}

        {/* JSON-LD Structured Data */}
        {jsonLd && (
          <script type="application/ld+json">
            {JSON.stringify(jsonLd)}
          </script>
        )}
      </Helmet>
      {children}
    </HelmetProvider>
  );
}

// Helper: crea JSON-LD per FAQPage
export function createFAQSchema(questions: { question: string; answer: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map(q => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.answer
      }
    }))
  };
}

// Helper: crea JSON-LD per WebSite (homepage)
export function createWebSiteSchema(
  name: string,
  description: string,
  searchAction?: { target: string; queryInput: string }
) {
  const schema: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name,
    url: 'https://sealevel.it',
    description,
    publisher: {
      '@type': 'Organization',
      name: 'Sea Level',
      url: 'https://sealevel.it'
    },
    potentialAction: searchAction ? {
      '@type': 'SearchAction',
      target: searchAction.target,
      'query-input': searchAction.queryInput
    } : undefined,
    areaServed: { '@type': 'Country', name: 'Italy' },
    serviceType: 'Marketplace B2B'
  };
  return schema;
}

// Helper: crea JSON-LD per Organization
export function createOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Sea Level',
    url: 'https://sealevel.it',
    logo: 'https://sealevel.it/wp-content/plugins/boat-trader-v3/assets/images/icon-192.png',
    description: 'Marketplace B2B per cantieri nautici e concessionari di natanti usati.',
    areaServed: 'Italia',
    serviceType: 'Piattaforma compravendita barche'
  };
}

// Helper: crea JSON-LD per Product (barca) - enhanced
export function createProductSchema(product: {
  name: string;
  description: string;
  image: string;
  images?: string[];
  price: number;
  priceWholesale?: number;
  brand: string;
  model?: string;
  year: number;
  length: number;
  beam?: number;
  draft?: number;
  weight?: number;
  availability: 'InStock' | 'SoldOut' | 'PreOrder';
  seller: string;
  engine?: string;
  enginePower?: number;
  fuelType?: string;
  fuelCapacity?: number;
  hours?: number;
  condition?: string;
  category?: string;
  homologation?: string;
  location?: string;
  region?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: product.image,
    images: product.images,
    brand: { '@type': 'Brand', name: product.brand },
    manufacturer: { '@type': 'Organization', name: product.brand },
    model: product.model,
    offers: {
      '@type': 'Offer',
      priceCurrency: 'EUR',
      price: product.price,
      priceSpecification: product.priceWholesale ? {
        '@type': 'PriceSpecification',
        minPrice: product.priceWholesale,
        maxPrice: product.price,
        price: product.price,
        priceCurrency: 'EUR'
      } : undefined,
      availability: `https://schema.org/${product.availability}`,
      seller: { '@type': 'Organization', name: product.seller }
    },
    additionalProperty: [
      { '@type': 'PropertyValue', name: 'Lunghezza', value: `${product.length} m` },
      { '@type': 'PropertyValue', name: 'Anno', value: product.year },
      { '@type': 'PropertyValue', name: 'Categoria', value: product.category || 'Barca a motore' },
      ...(product.beam ? [{ '@type': 'PropertyValue', name: 'Baglio', value: `${product.beam} m` }] : []),
      ...(product.draft ? [{ '@type': 'PropertyValue', name: 'Pescaggio', value: `${product.draft} m` }] : []),
      ...(product.weight ? [{ '@type': 'PropertyValue', name: 'Peso', value: `${product.weight} kg` }] : []),
      ...(product.engine ? [{ '@type': 'PropertyValue', name: 'Motore', value: product.engine }] : []),
      ...(product.enginePower ? [{ '@type': 'PropertyValue', name: 'Potenza', value: `${product.enginePower} HP` }] : []),
      ...(product.fuelType ? [{ '@type': 'PropertyValue', name: 'Carburante', value: product.fuelType }] : []),
      ...(product.fuelCapacity ? [{ '@type': 'PropertyValue', name: 'Serbatoio Carburante', value: `${product.fuelCapacity} L` }] : []),
      ...(product.hours ? [{ '@type': 'PropertyValue', name: 'Ore Motore', value: product.hours }] : []),
      ...(product.condition ? [{ '@type': 'PropertyValue', name: 'Condizione', value: product.condition }] : []),
      ...(product.homologation ? [{ '@type': 'PropertyValue', name: 'Omologazione', value: product.homologation }] : []),
      ...(product.location ? [{ '@type': 'PropertyValue', name: 'Località', value: product.location }] : []),
      ...(product.region ? [{ '@type': 'PropertyValue', name: 'Regione', value: product.region }] : [])
    ]
  };
}

// Helper: crea JSON-LD per CollectionPage (listings)
export function createCollectionPageSchema(options: {
  name: string;
  description: string;
  url: string;
  items: {
    id: number;
    name: string;
    image: string;
    price: number;
    url: string;
  }[];
  numberOfItems: number;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: options.name,
    description: options.description,
    url: options.url,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: options.numberOfItems,
      itemListElement: options.items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: item.url,
        name: item.name,
        image: item.image,
        offers: {
          '@type': 'Offer',
          priceCurrency: 'EUR',
          price: item.price
        }
      }))
    }
  };
}

// Helper: crea JSON-LD per LocalBusiness (dealer)
export function createLocalBusinessSchema(dealer: {
  name: string;
  description: string;
  image?: string;
  address: string;
  city: string;
  region: string;
  lat?: number;
  lng?: number;
  phone?: string;
  email?: string;
  url?: string;
  priceRange?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: dealer.name,
    description: dealer.description,
    image: dealer.image,
    address: {
      '@type': 'PostalAddress',
      streetAddress: dealer.address,
      addressLocality: dealer.city,
      addressRegion: dealer.region,
      addressCountry: 'IT'
    },
    ...(dealer.lat && dealer.lng && {
      geo: {
        '@type': 'GeoCoordinates',
        latitude: dealer.lat,
        longitude: dealer.lng
      }
    }),
    telephone: dealer.phone,
    email: dealer.email,
    url: dealer.url,
    priceRange: dealer.priceRange || '€€'
  };
}

// Helper: crea JSON-LD per ItemList (similar products)
export function createItemListSchema(options: {
  name: string;
  items: {
    id: number;
    name: string;
    image: string;
    price: number;
    url: string;
  }[];
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: options.name,
    itemListElement: options.items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: item.url,
      name: item.name,
      image: item.image,
      offers: {
        '@type': 'Offer',
        priceCurrency: 'EUR',
        price: item.price
      }
    }))
  };
}
